import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Clock, GraduationCap, CheckCircle2, ArrowLeft, ListChecks, Wallet } from "lucide-react";
import PageHero from "../components/PageHero";
import CourseEnrollmentForm from "../components/CourseEnrollmentForm";
import { EditableText } from "../components/live/Editable";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import api from "../lib/api";

const categoryLabels = {
  long_term: "Long Term Courses",
  short_term: "Short Term Courses",
  corporate: "Corporate Training",
  earn_while_learn: "Earn While You Learn",
};

const LONG_TERM_FAQ = [
  {
    q: "After 10th, after 12th, what next?",
    a: "Students who have passed their 10th or 12th class may apply to the advanced level courses, imparted through the German system of berufsschule or work-study model. The courses of Tool & Die Making (Design and Manufacture), Precision Machining (with CAD / CAM) are mechanical in nature, whilst the course of Mechatronics is a combination of mechanical, electrical and electronics discipline.",
  },
  {
    q: "What is the German system of Berufsschule or Work-study model?",
    a: "Acknowledged as one of the best training systems in the World, the German system of berufsschule or work-study model, is where students learn simultaneously at the institute and also at a company. This results in students learning theoretically and practically, thus creating a highly sophisticated and advanced workforce, who can grow to become a major contributor to the Company. In Germany, several companies such as Bayer, Deutsche Telecom, Deutsche Bank, Mercedes etc. are amongst several thousands of small, medium and large companies whose top management such as CEO, Chairman have been persons who started their careers training in the work-study model. Moreover, this system of learning and development also provides students with key entrepreneurial skills, one of the main reasons why 90% of German companies are family run businesses or entrepreneurial.",
  },
  {
    q: "Why should I learn in the Work-Study model?",
    a: "The work-study model has a unique 2-day institute, 4-day company training curriculum. This results in the student learning theoretically and practically. In the highly competitive job market, students with theoretical & practical knowledge have great advantage over those who primarily learn theoretically. Moreover, in addition to technical areas, since learning includes subjects such as marketing, accounts, compliances etc., several students wishing to run their family business or become entrepreneurs find this course beneficial.",
  },
  {
    q: "Do I Earn While I Learn?",
    a: "Students who are 18 yrs old, maintain 85% attendance, have atleast 50% marks and obtain a pass in soft skills, have the opportunity in the 2nd year to apply to various companies who partner with the institute for the work-study model. While training at the company, students receive a training allowance. So students, from the second year onwards, you can Earn While You Learn.",
  },
];

const SHORT_TERM_QUESTIONS = [
  "Have you been looking for a job but are still Unemployed?",
  "Or do you already have a job but feel this is not the right job for you?",
  "Are you thinking of how to get a better paying job or a job with a better designation?",
  "Are you wondering about how to join a multinational company?",
  "Do you want to start your own company?",
];

function CategoryIntro({ filter }) {
  if (filter === "long_term") {
    return (
      <div className="mb-12 rounded-3xl bg-slate-50 border border-slate-100 p-7 sm:p-10" data-testid="courses-intro-long-term">
        <p className="kicker mb-2">Long Term Courses After 10th or 12th</p>
        <h2 className="text-2xl font-bold text-[#0B2A6B] mb-6">(3 ½ years courses)</h2>
        <Accordion type="single" collapsible className="w-full">
          {LONG_TERM_FAQ.map((f, i) => (
            <AccordionItem key={f.q} value={`item-${i}`}>
              <AccordionTrigger className="text-left font-bold text-[#0B2A6B] hover:text-yellow-600" data-testid={`courses-faq-${i}`}>
                <EditableText path={`courses.longterm.faq.${i}.q`} as="span">{f.q}</EditableText>
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                <EditableText path={`courses.longterm.faq.${i}.a`} as="span" multiline>{f.a}</EditableText>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <Link
          to="/courses/earn-while-you-learn"
          className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-yellow-400 text-[#0B2A6B] font-bold text-sm hover:bg-yellow-500 transition"
          data-testid="courses-ewyl-more-link"
        >
          For more information on Earn While You Learn Click Here <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }
  if (filter === "short_term") {
    return (
      <div className="mb-12 rounded-3xl bg-slate-50 border border-slate-100 p-7 sm:p-10" data-testid="courses-intro-short-term">
        <p className="kicker mb-2">Short Term Courses</p>
        <EditableText path="courses.shortterm.title" as="h2" className="text-2xl font-bold text-[#0B2A6B] mb-4 block">Why learn a short term course?</EditableText>
        <EditableText path="courses.shortterm.text1" as="p" multiline className="text-slate-600 leading-relaxed mb-4 block">
          Are you one of those people who have done their ITI, Diploma or Degree and now need some help to take their career forward?
        </EditableText>
        <ul className="space-y-2 mb-5">
          {SHORT_TERM_QUESTIONS.map((q, i) => (
            <li key={q} className="flex items-start gap-2.5 text-slate-700">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-yellow-400 shrink-0" />
              <EditableText path={`courses.shortterm.question.${i}`} as="span">{q}</EditableText>
            </li>
          ))}
        </ul>
        <p className="text-slate-600 leading-relaxed mb-4">
          If the answer to any of the above questions is <strong className="text-[#0B2A6B]">Yes</strong>, then a
          short term course can help you.
        </p>
        <EditableText path="courses.shortterm.text2" as="p" multiline className="text-slate-600 leading-relaxed block">
          Short term courses are extremely useful for people who have already got a recognized qualification such as ITI or Degree or Diploma and are now looking at how to get the right job and build their career. Short term courses give the Participant the knowledge and skill he/she requires to move their careers ahead. These kind of knowledge and skills include certain theoretical knowledge, practical skills, behavioral skills, job hunting and interviewing skills etc.
        </EditableText>
      </div>
    );
  }
  if (filter === "corporate") {
    return (
      <div className="mb-12 rounded-3xl bg-[#0B2A6B] text-white p-7 sm:p-10" data-testid="courses-intro-corporate">
        <p className="inline-block px-3 py-1 rounded-full bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-xs font-bold tracking-wider uppercase mb-4">
          Professional Courses
        </p>
        <blockquote className="text-xl sm:text-2xl font-bold mb-2">
          <EditableText path="courses.corporate.quote" as="span">"If you think training is expensive, try ignorance."</EditableText>
        </blockquote>
        <EditableText path="courses.corporate.quoteBy" as="p" className="text-yellow-400 font-semibold mb-5 block">– Peter Drucker</EditableText>
        <EditableText path="courses.corporate.text1" as="p" multiline className="text-slate-200 leading-relaxed mb-4 block">
          The above, quite simply sums it up. As an institute for professional courses, we work with several companies to tailor make training solutions for them. Training can be conducted at our institute or at your company premises. Our very experienced training faculty designs the course as per the need of your company / profession. Some of the top multi-national companies that we conduct training for are: Maruti Suzuki, IFB, Subros, Becton Dickenson, Asahi, Honda and many others.
        </EditableText>
        <p className="text-slate-200 leading-relaxed">
          To discuss these and other needs that your company may have, please call our corporate training cell
          on: <a href="tel:+919953848338" className="font-bold text-yellow-400">9953848338</a> or{" "}
          <a href="tel:+918800288994" className="font-bold text-yellow-400">8800288994</a>. Alternatively, email
          your requirements to:{" "}
          <a href="mailto:info@kikicentre.com" className="font-bold text-yellow-400 italic">info@kikicentre.com</a>
        </p>
      </div>
    );
  }
  return null;
}

export default function CoursesPage() {
  const { category } = useParams();
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState(category || "all");

  useEffect(() => {
    api.get("/courses", { params: { active_only: true } }).then((r) => setCourses(r.data));
  }, []);

  useEffect(() => {
    if (category) {
      const key = category.replace(/-/g, "_");
      setFilter(key);
    } else {
      setFilter("all");
    }
  }, [category]);

  const filtered = filter === "all" ? courses : courses.filter((c) => c.category === filter);

  const tabs = [
    { key: "all", label: "All Courses" },
    { key: "long_term", label: "Long Term" },
    { key: "short_term", label: "Short Term" },
    { key: "corporate", label: "Corporate" },
    { key: "earn_while_learn", label: "Earn While Learn", to: "/courses/earn-while-you-learn" },
  ];

  return (
    <>
      <PageHero
        kicker="Our Courses"
        title={categoryLabels[filter] || "Explore Our Technical Courses"}
        subtitle="German-certified curriculum, hands-on labs and strong placement support across all our programs."
      />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-10">
            {tabs.map((t) =>
              t.to ? (
                <Link
                  key={t.key}
                  to={t.to}
                  className="px-4 py-2 text-sm font-semibold rounded-full transition border bg-yellow-400 text-[#0B2A6B] border-yellow-400 hover:bg-yellow-500"
                >
                  {t.label}
                </Link>
              ) : (
                <button
                  key={t.key}
                  onClick={() => setFilter(t.key)}
                  className={`px-4 py-2 text-sm font-semibold rounded-full transition border ${
                    filter === t.key
                      ? "bg-[#0B2A6B] text-white border-[#0B2A6B]"
                      : "bg-white text-slate-700 border-slate-200 hover:border-[#0B2A6B] hover:text-[#0B2A6B]"
                  }`}
                >
                  {t.label}
                </button>
              )
            )}
          </div>

          <CategoryIntro filter={filter} />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {filtered.map((c, i) => {
              const Icon = Icons[c.icon] || Icons.GraduationCap;
              return (
                <Link
                  key={c.id}
                  to={`/courses/detail/${c.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-slate-100 tilt-card animate-fade-up"
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img src={c.image_url} alt={c.title} className="absolute inset-0 w-full h-full object-cover zoom-img" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-yellow-400 text-[#0B2A6B] text-[11px] font-bold uppercase">
                      {c.category.replace("_", " ")}
                    </span>
                    <div className="absolute bottom-4 left-4 flex items-center gap-1 text-white text-xs font-medium">
                      <Clock className="h-3.5 w-3.5" />
                      {c.duration}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="h-10 w-10 rounded-lg bg-yellow-400/15 group-hover:bg-yellow-400 transition flex items-center justify-center">
                        <Icon className="h-5 w-5 text-yellow-600 group-hover:text-[#0B2A6B] transition" />
                      </div>
                      <h3 className="font-bold text-[#0B2A6B] text-lg leading-snug group-hover:text-yellow-600 transition">
                        {c.title}
                      </h3>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-3">{c.description}</p>
                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-xs text-slate-500">{c.eligibility}</span>
                      <span className="text-yellow-600 font-bold text-sm inline-flex items-center gap-1 group-hover:translate-x-1 transition">
                        Details <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16 text-slate-500">No courses available in this category.</div>
          )}
        </div>
      </section>
    </>
  );
}

export function CourseDetailPage() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/courses/slug/${slug}`)
      .then((r) => setCourse(r.data))
      .catch(() => setCourse(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 text-center">Loading...</div>;
  if (!course) return <div className="py-32 text-center">Course not found.</div>;

  const Icon = Icons[course.icon] || GraduationCap;

  return (
    <>
      <PageHero kicker={course.category.replace("_", " ")} title={course.title} subtitle={course.description} />

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/courses" className="inline-flex items-center gap-1 text-[#0B2A6B] font-semibold hover:text-yellow-600 mb-8">
            <ArrowLeft className="h-4 w-4" /> Back to all courses
          </Link>

          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <div className="rounded-2xl overflow-hidden mb-8 shadow-lg">
                <img src={course.image_url} alt={course.title} className="w-full h-80 object-cover" />
              </div>
              <h2 className="text-2xl font-bold text-[#0B2A6B] mb-4">About this course</h2>
              <p className="text-slate-700 leading-relaxed mb-8">{course.description}</p>

              <h3 className="text-xl font-bold text-[#0B2A6B] mb-4">Highlights</h3>
              <ul className="grid sm:grid-cols-2 gap-3 mb-8">
                {course.highlights?.map((h) => (
                  <li key={h} className="flex items-start gap-3 p-4 rounded-xl bg-yellow-50 border border-yellow-100">
                    <CheckCircle2 className="h-5 w-5 text-yellow-600 mt-0.5 shrink-0" />
                    <span className="font-medium text-slate-700">{h}</span>
                  </li>
                ))}
              </ul>

              {course.syllabus?.length > 0 && (
                <div className="mb-8" data-testid="course-syllabus">
                  <h3 className="text-xl font-bold text-[#0B2A6B] mb-4 flex items-center gap-2">
                    <ListChecks className="h-5 w-5 text-yellow-600" /> Syllabus &amp; Curriculum
                  </h3>
                  <ol className="space-y-2">
                    {course.syllabus.map((item, idx) => (
                      <li key={item} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 bg-white">
                        <span className="h-6 w-6 shrink-0 rounded-full bg-[#0B2A6B] text-white text-xs font-bold flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <span className="text-slate-700 text-sm font-medium">{item}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {course.fees && (
                <div className="mb-4 rounded-2xl border border-yellow-200 bg-yellow-50 p-6" data-testid="course-fees">
                  <h3 className="text-lg font-bold text-[#0B2A6B] mb-2 flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-yellow-600" /> Course Fees
                  </h3>
                  <p className="text-slate-700 text-sm leading-relaxed">{course.fees}</p>
                </div>
              )}
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-28 space-y-5">
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="bg-[#0B2A6B] p-6 text-white">
                    <div className="h-14 w-14 rounded-xl bg-yellow-400 flex items-center justify-center mb-3">
                      <Icon className="h-7 w-7 text-[#0B2A6B]" />
                    </div>
                    <h3 className="text-xl font-bold">{course.title}</h3>
                  </div>
                  <div className="p-6 space-y-4">
                    <Row label="Category" value={course.category.replace("_", " ")} />
                    <Row label="Duration" value={course.duration} />
                    <Row label="Eligibility" value={course.eligibility} />
                    <Link
                      to="/admissions"
                      className="block text-center w-full px-6 py-3 rounded-lg btn-primary mt-2"
                      data-testid="course-apply-btn"
                    >
                      Apply for Admission
                    </Link>
                  </div>
                </div>

                <div data-testid="course-enquiry">
                  <CourseEnrollmentForm courseSlug={course.slug} courseTitle={course.title} />
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-500 font-medium">{label}</span>
      <span className="text-[#0B2A6B] font-semibold capitalize">{value || "—"}</span>
    </div>
  );
}
